import dayjs from "dayjs";
import {
    getFullUnassignedPlanByRouteId,
    getKpiGetPointProgress,
    getPlanGetByPointId,
    thunkCall,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { PlanFinGroupDetail, PointProductProgress } from "tm-shared/types";
import { arrayToRecord } from "tm-shared/utils";

interface GetPlansByMarketIdThunkResponse {
    [pointId: string]: {
        [finGroup: string]: PlanFinGroupDetail;
    };
}

export const getPlansByPointIdThunk = thunkCall<
    GetPlansByMarketIdThunkResponse,
    { pointIds: string[]; routeId: string }
>(
    "getPlansByPointId",
    async ({ pointIds, routeId }: { pointIds: string[]; routeId: string }) => {
        const BATCH_SIZE = 10;

        const today = dayjs().format(backendDateFormat.date);
        const result: GetPlansByMarketIdThunkResponse = {};

        try {
            const response = await getFullUnassignedPlanByRouteId({
                routeId,
                targetDate: today,
            });

            if (response?.result) {
                result.unassignedPlan = response.result.settings.items;
            }
        } catch (error) {
            console.warn("getFullUnassignedPlanByRouteId failed:", error);
        }

        for (let i = 0; i < pointIds.length; i += BATCH_SIZE) {
            const batch = pointIds.slice(i, i + BATCH_SIZE);

            const responses = await Promise.allSettled(
                batch.map(async (pointId: string) => {
                    const response = await getPlanGetByPointId({
                        pointId,
                        targetDate: today,
                    });

                    return { pointId, response };
                }),
            );

            responses.forEach((s) => {
                if (s.status !== "fulfilled") return;

                const { pointId, response } = s.value;

                if (response?.result) {
                    result[pointId] = {
                        ...response.result.settings.items,
                    };
                }
            });
        }

        return result;
    },
);

// ---------------------------------------------------

interface GetKpiGetMarketProgressesThunkResponse {
    [pointId: string]: {
        [barcode: string]: PointProductProgress;
    };
}

export const getKpiGetPointProgressesThunk = thunkCall<
    GetKpiGetMarketProgressesThunkResponse,
    { pointIds: string[] }
>("getKpiGetMarketProgresses", async ({ pointIds }: { pointIds: string[] }) => {
    const BATCH_SIZE = 10;

    const today = dayjs().format(backendDateFormat.date);
    const result: GetKpiGetMarketProgressesThunkResponse = {};

    for (let i = 0; i < pointIds.length; i += BATCH_SIZE) {
        const batch = pointIds.slice(i, i + BATCH_SIZE);

        const responses = await Promise.allSettled(
            batch.map(async (pointId) => {
                const response = await getKpiGetPointProgress({
                    pointId,
                    targetDate: today,
                });

                return { pointId, response };
            }),
        );

        responses.forEach((s) => {
            if (s.status !== "fulfilled") return;

            const { pointId, response } = s.value;

            if (response?.result) {
                result[pointId] = arrayToRecord(response.result, "barcode");
            }
        });
    }

    return result;
});
